import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Theme, Toast } from '../types';

interface UIState {
  // Theme
  theme: Theme;
  resolvedTheme: 'light' | 'dark';

  // Loading states
  isGenerating: boolean;
  isExporting: boolean;

  // Modals
  activeModal: string | null;
  modalData: unknown;

  // Toasts
  toasts: Toast[];

  // Debug
  showDebugPanel: boolean;

  // Actions
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  setIsGenerating: (loading: boolean) => void;
  setIsExporting: (loading: boolean) => void;
  openModal: (modalId: string, data?: unknown) => void;
  closeModal: () => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  toggleDebugPanel: () => void;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      resolvedTheme: getSystemTheme(),
      isGenerating: false,
      isExporting: false,
      activeModal: null,
      modalData: null,
      toasts: [],
      showDebugPanel: false,

      setTheme: (theme) => {
        const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;
        set({ theme, resolvedTheme });

        // Update document class for Tailwind dark mode
        if (typeof document !== 'undefined') {
          document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
        }
      },

      toggleTheme: () => {
        const { theme } = get();
        const newTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
        get().setTheme(newTheme);
      },

      setIsGenerating: (isGenerating) => set({ isGenerating }),

      setIsExporting: (isExporting) => set({ isExporting }),

      openModal: (modalId, data = null) =>
        set({ activeModal: modalId, modalData: data }),

      closeModal: () => set({ activeModal: null, modalData: null }),

      addToast: (toast) => {
        const id = generateId();
        const newToast: Toast = { ...toast, id };

        set((state) => ({
          toasts: [...state.toasts, newToast],
        }));

        // Auto-remove after duration
        const duration = toast.duration || 5000;
        setTimeout(() => {
          get().removeToast(id);
        }, duration);
      },

      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      clearToasts: () => set({ toasts: [] }),

      toggleDebugPanel: () =>
        set((state) => ({ showDebugPanel: !state.showDebugPanel })),
    }),
    {
      name: 'wiscv-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        showDebugPanel: state.showDebugPanel,
      }),
      // Po rehydracji przelicz resolvedTheme z zapisanego wyboru:
      // zapisany wybór użytkownika > prefers-color-scheme > light.
      merge: (persistedState, currentState) => {
        const merged = {
          ...currentState,
          ...(persistedState as Partial<UIState> | undefined),
        };
        merged.resolvedTheme =
          merged.theme === 'system' ? getSystemTheme() : merged.theme;
        return merged;
      },
    }
  )
);

// Listen for system theme changes
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme } = useUIStore.getState();
    if (theme === 'system') {
      useUIStore.getState().setTheme('system');
    }
  });
}
